import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { polishChapter } from "@/lib/rewriteChapter";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Check, X, Coins } from "lucide-react";

const COST = 4;

function PolishPreview({ result, onKeep, onDiscard, busy }) {
  return (
    <div className="space-y-3" data-testid="polish-preview">
      <p className="text-xs text-muted-foreground">
        {result.word_count.toLocaleString()} words (was {result.original_word_count.toLocaleString()}). Keep it to replace the
        chapter — your current version goes to <b>History</b> — or discard it and nothing changes.
      </p>
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-border bg-background p-4 text-sm leading-relaxed space-y-3 [&_p]:my-0"
        data-testid="polish-preview-text" dangerouslySetInnerHTML={{ __html: result.result }} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDiscard} disabled={busy} className="gap-1.5" data-testid="polish-discard"><X className="w-4 h-4" /> Discard</Button>
        <Button onClick={onKeep} disabled={busy} className="gap-1.5" data-testid="polish-keep">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Keep polished version
        </Button>
      </div>
    </div>
  );
}

export default function PolishChapterDialog({ open, onOpenChange, projectId, chapter, hasBrief, onBeforeStart, onDone, onOpenBrief }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setNotes(""); setBusy(false); setResult(null); } }, [open]);

  const base = `/projects/${projectId}/chapters/${chapter?.id}/polish`;
  const start = async () => {
    setBusy(true);
    try {
      await onBeforeStart?.();
      setResult(await polishChapter(projectId, chapter.id, { notes: notes.trim() }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Polish failed — please try again"));
    } finally { setBusy(false); }
  };
  const keep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/apply`);
      onDone?.({ chapter: data, content: data.content, word_count: data.word_count });
      toast.success("Polished version kept — the previous text is in History");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't apply the polish"); }
    finally { setBusy(false); }
  };
  const discard = async () => {
    try { await api.post(`${base}/discard`); } catch { /* nothing to undo */ }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) (v ? onOpenChange(v) : result ? discard() : onOpenChange(false)); }}>
      <DialogContent className={result ? "max-w-3xl" : "max-w-lg"} data-testid="polish-chapter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Polish with GPT</DialogTitle>
          <DialogDescription>
            A light-touch rewrite of <b>“{chapter?.title || "this chapter"}”</b> by OpenAI's latest model: same events and
            ending, better voice, rhythm and dialogue — guided by your style brief, then checked for continuity.
          </DialogDescription>
        </DialogHeader>
        {result ? <PolishPreview result={result} onKeep={keep} onDiscard={discard} busy={busy} /> : (
          <div className="space-y-4">
            {!hasBrief && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-muted-foreground" data-testid="polish-no-brief">
                No style brief yet — polish works best with one.{" "}
                <button type="button" className="text-primary underline" onClick={onOpenBrief} data-testid="polish-open-brief">Add a style brief</button>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Anything specific? <span className="text-muted-foreground/70">(optional)</span></label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1" data-testid="polish-notes"
                placeholder="e.g. Make Dean talk more and Bob more cutting. Keep the last two lines exactly." />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="polish-cost">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" /> Polishing one chapter</span>
              <span className="text-sm font-semibold">{COST} credits</span>
            </div>
            <Button className="w-full gap-2" onClick={start} disabled={busy || !chapter?.id} data-testid="polish-start">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Polishing… (1–3 min)" : "Polish chapter"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
